<template>
    <b-form class="onboarding-form mx-auto">
        <b-row>
            <b-col
                    sm="12"
                    md="6"
                    class="mb-4 mb-md-0"
            >
                <b-form-group
                        label="First Name"
                        label-class="label"
                >
                    <b-form-input
                            v-model="internalModel.firstName"
                            type="text"
                            autocomplete="off"
                            maxlength="225"
                            @input="clearErrors('firstName')"
                    />

                    <template v-if="responseErrors.firstName">
                        <b-form-invalid-feedback
                                :key="errIndex + 'firstNameErrors'"
                                class="d-block"
                                v-for="(error, errIndex) in responseErrors.firstName"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>

                    <form-errors
                            :errors="formErrors"
                            field="firstName"
                    />
                </b-form-group>
            </b-col>

            <b-col
                    sm="12"
                    md="6"
            >
                <b-form-group
                        label="Last Name"
                        label-class="label"
                        @input.stop=""
                >
                    <b-form-input
                            v-model="internalModel.lastName"
                            type="text"
                            autocomplete="off"
                            maxlength="225"
                            @input="clearErrors('lastName')"
                    />

                    <template v-if="responseErrors.lastName">
                        <b-form-invalid-feedback
                                :key="errIndex + 'lastNameErrors'"
                                class="d-block"
                                v-for="(error, errIndex) in responseErrors.lastName"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>

                    <form-errors
                            :errors="formErrors"
                            field="lastName"
                    />
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b-form-group
                        label="Email Address"
                        label-class="label"
                >
                    <b-form-input
                            v-model="internalModel.email"
                            type="email"
                            id="email"
                            maxlength="225"
                            name="email"
                            :disabled="isExclusiveMember"
                    />

                    <template v-if="responseErrors.email">
                        <b-form-invalid-feedback
                                :key="errIndex + 'lastNameErrors'"
                                class="d-block"
                                v-for="(error, errIndex) in responseErrors.email"
                        >
                            <i class="fal fa-exclamation-circle w-auto" />
                            {{ error.replace('!', '') }}
                        </b-form-invalid-feedback>
                    </template>

                    <form-errors
                            :errors="formErrors"
                            field="email"
                    />
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col
                    sm="12"
                    md="6"
                    class="mb-4 mb-md-0"
            >
                <b-form-group
                        label="Mobile Phone"
                        label-class="label"
                >
                    <b-form-input
                            v-model="internalModel.phone"
                            type="text"
                            :formatter="formatNumber"
                            autocomplete="off"
                    />

                    <form-errors
                            :errors="formErrors"
                            field="phone"
                    />
                </b-form-group>
            </b-col>

            <b-col
                    sm="12"
                    md="6"
            >
                <second-level-select
                        label="Occupation"
                        :placeholder="occupationLabel"
                        label-on-top
                        full-width-dropdown
                        dropdown-title="INDUSTRY"
                        child-dropdown-title="OCCUPATION"
                        :options="industries"
                        child-key-identifier="occupations"
                        child-label-key="title"
                        child-value-key="id"
                        :parent-value="internalModel.industry"
                        :child-value="internalModel.occupation"
                        @event-selected="selectOccupationAndIndustry"
                />
            </b-col>
        </b-row>

        <b-row v-if="showLicenseInput">
            <b-col
                    sm="12"
                    md="6"
                    class="mb-4 mb-md-0"
            >
                <b-form-group
                        label="Professional License"
                        label-class="label"
                >
                    <custom-select
                            v-if="renderStates"
                            id="contact-states-select"
                            :options="states"
                            :reduce="option => option.abbreviation"
                            clearable
                            cs-label="state"
                            placeholder="State"
                            :async="false"
                            v-model="internalModel.licenseState"
                    />

                    <form-errors
                            :errors="formErrors"
                            field="licenseState"
                    />
                </b-form-group>
            </b-col>

            <b-col
                    sm="12"
                    md="6"
            >
                <b-form-group
                    label=" "
                    label-class="label"
                >
                    <b-form-input
                            class="contact-license-input"
                            v-model.trim="internalModel.licenseNumber"
                            placeholder="License Number"
                            autocomplete="off"
                    />
                    <form-errors
                            field="licenseNumber"
                            :errors="formErrors"
                    />
                </b-form-group>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <b-form-group
                        label="Company or Team Name - optional"
                        label-class="label"
                >
                    <b-form-input
                            v-model="internalModel.meta.company"
                            type="text"
                            id="company"
                            maxlength="225"
                            name="company"
                    />

                    <form-errors
                            :errors="formErrors"
                            field="company"
                    />
                </b-form-group>
            </b-col>
        </b-row>

        <b-row v-if="!isExclusiveMember">
            <b-col>
                <b-form-group
                        label="Password"
                        label-class="label"
                >
                    <b-input-group>
                        <b-input-group-prepend>
                <span>
                   <i class="far fa-lock-alt" />
                </span>
                        </b-input-group-prepend>

                        <b-form-input
                                id="password-input"
                                v-model="internalModel.password"
                                type="password"
                                @focus="showPasswordTooltip = true"
                                @focusout="showPasswordTooltip = false"
                                class="border-left-0"
                        />

                        <password-validation
                                :password="internalModel.password"
                                target="password-input"
                                :visible="isPasswordValidationVisible"
                        />
                    </b-input-group>
                </b-form-group>
            </b-col>
        </b-row>

        <b-form-checkbox
                class="agreement-checkbox"
                v-model="termsAccepted"
                required
        >
            By clicking ‘Finish’, you consent to and agree that you have read and are legally bound by the
            <b-link
                    class="text-link"
                    target="_blank"
                    :href="termsOfUseLink"
            >
                Terms of Use
            </b-link>
            and
            <b-link
                    class="text-link"
                    target="_blank"
                    :href="privacyPolicyUrl"
            >
                Privacy Policy
            </b-link>
        </b-form-checkbox>

        <b-form-group class="btn-wrapper text-center">
            <b-button
                    class="save-btn mx-auto w-100"
                    variant="primary"
                    :disabled="disabledFinishButton"
                    @click="handleSubmit"
            >
                Finish
            </b-button>
        </b-form-group>
    </b-form>
</template>

<script>
import "./OnboardingForm.scss";
import {mapGetters} from "vuex";
import PasswordValidation from "../PasswordValidationTooltip.vue";
import {IDS} from "@milestoneslabs/milestones-core-js/src/AppResources/LuOrganizationOccupationAppResource";
import ShowNotificationMixin from "../../../mixins/ShowNotificationMixin";
import {privacyPolicy, termsOfUse} from "../../../helpers/externalLinks";
import InternalFormErrosMixin from "../../../mixins/InternalFormErrorsMixin";
import FormErrors from "../../FormErrors/FormErrors.vue";
import VModel from "../../../mixins/VModel.js";
import {StateAppResourceSearch} from "@milestoneslabs/milestones-core-js/src/AppResourceSearch/StateAppResourceSearch";
import {MORTGAGE_LOAN_ORIGINATOR} from "@milestoneslabs/milestones-core-js/src/AppResources/ProfessionalLicenseAppResource";
import issuingBodyMap from "../../../helpers/issuingBodyMap";
import SecondLevelSelect from '../../../components/SecondLevelSelect/SecondLevelSelect.vue';

export default {
    name: "OnboardingForm",
    components: {
        PasswordValidation,
        FormErrors,
        SecondLevelSelect,
    },

    mixins: [
        ShowNotificationMixin,
        InternalFormErrosMixin,
        VModel,
    ],

    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        isExclusiveMember: {
            type: Boolean,
            default: false,
        },
        formErrors: {
            type: Object,
            default: () => {
                return {};
            },
        },
        responseErrors: {
            type: Object,
            default: () => {
                return {};
            },
        },
        industries: {
            type: Array
        },
    },

    data() {
        return {
            termsAccepted: false,
            showPasswordTooltip: false,
            termsOfUseLink: termsOfUse,
            REAL_ESTATE_AGENT: IDS.REAL_ESTATE_AGENT,
            stateSearch: new StateAppResourceSearch('state'),
            states: [],
            renderStates: false,
        };
    },

    computed: {
        requestFilters() {
            return {pagination: 'disabled'};
        },

        isPasswordValidationVisible() {
            return this.showPasswordTooltip || this.formErrors.hasOwnProperty('password');
        },

        showLicenseInput() {
            return IDS.LOAN_OFFICER === this.internalModel.occupation;
        },
        privacyPolicyUrl() {
            return sessionStorage.getItem('organizationPrivacyPolicyUrl') || privacyPolicy;
        },
        disabledFinishButton() {
            const hasAllRequiredFieldsSet = (
                    !!this.internalModel.firstName?.trim()
                    && !!this.internalModel.lastName?.trim()
                    && !!this.internalModel.email?.trim()
                    && !!this.internalModel.phone?.trim()
                    && !!this.internalModel.occupation
                    && (this.isExclusiveMember || !!this.internalModel.password?.trim())
            )

            return this.disabled || !this.termsAccepted || !hasAllRequiredFieldsSet
        },
        occupationLabel() {
            for (const industry of this.industries) {
                for (const occupation of industry?.occupations || []) {
                    if (occupation.id === this.internalModel.occupation) {
                        return occupation.title;
                    }
                }
            }

            return 'Occupation';
        }
    },

    methods: {
        formatNumber(value) {
            if (value) {
                let x = value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
                value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
            }
            return value;
        },

        handleSubmit() {
            this.$emit('submit');
        },

        async fetchStates() {
            try {
                this.renderStates = false;
                await this.stateSearch.search(true);
            } catch (e) {
                console.warn(e);
                this.showNotification({
                    message: 'Failed to fetch states. Please try again later.',
                    title: 'Error',
                    type: 'error',
                });
            }
        },
        getIssuingBody(state) {
            return issuingBodyMap[MORTGAGE_LOAN_ORIGINATOR][state];
        },
        selectOccupationAndIndustry($event) {
            this.internalModel.industry = $event.parentValue;
            this.internalModel.occupation = $event.childValue;
        }
    },

    async beforeMount() {
        await this.fetchStates();
    },

    watch: {
        'stateSearch' : {
            handler: function (value) {
                this.$nextTick(() => {
                    this.renderStates = true;
                });
                this.states = value.appResourceCollection.toArray();
            },
            deep: true
        },
        'value.licenseState' : {
            handler: function (value) {
                if (!value) {
                    return;
                }
                this.internalModel.licenseType = MORTGAGE_LOAN_ORIGINATOR;
                this.internalModel.licenseIssuingBody = this.getIssuingBody(value);
            },
        },
        'value.occupation': {
            handler(newVal) {
                if (IDS.LOAN_OFFICER !== newVal) {
                    this.internalModel.licenseState = null;
                    this.internalModel.licenseNumber = null;
                    this.internalModel.licenseType = null;
                    this.internalModel.licenseIssuingBody = null;
                }
            }
        }
    }
};
</script>
